import { add, isBefore, parse } from 'date-fns';
import Swal from 'sweetalert2';

export class NewsletterForm {
  #form;
  #debug;
  #validSubmitDate;
  #submitButton;

  constructor(element, debug) {
    const formElement = element.closest('.newsletter-form') || element;
    if (!formElement.classList.contains('newsletter-form')) return;
    this.#form = formElement;
    this.#debug = debug;
    this.#init();
  }

  #init() {
    if (this.#debug) console.log('initializing newsletter form...');

    const spamThreshold = 3;
    const dateString = this.#form.querySelector('[name="newsletter-d"]')?.value;
    const dateToCheck = dateString ? parse(dateString, 'dd-MM-yyyy HH:mm:ss', new Date()) : new Date();
    this.#validSubmitDate = add(dateToCheck, { seconds: spamThreshold });

    this.#submitButton = this.#form.querySelector('[type="submit"]');
    this.#setEvents();
  }

  #setEvents() {
    this.#submitButton.addEventListener('click', e => {
      e.preventDefault();
      this.#validate();
    });
  }

  #isSpam() {
    return isBefore(new Date(), this.#validSubmitDate);
  }

  #isEmail(val) {
    // Very basic validation which only checks for: [one or more chars] @ [one or more chars] . [one or more chars]
    const regex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    return regex.test(val);
  }

  #getCallbackMessage(status, newsletterID, email) {
    switch (status) {
      case 'missing':
        return 'Geen e-mailadres ingevuld.';
      case 'invalid':
        return 'Ongeldig e-mailadres ingevuld.';
      case 'unallowed':
        return 'Dit e-mailadres is niet toegestaan.';
      case 'inactive':
        return `Er bestaat al een aanmelding met dit e-mailadres, maar deze is inactief.<br>
                Er werd zojuist een nieuwe activatiemail naar dit e-mailadres gestuurd.`;
      case 'unconfirmed':
        return `Er bestaat al een aanmelding met dit e-mailadres, maar deze is nog niet bevestigd.<br>
                Er werd zojuist opnieuw een bevestigingsmail naar dit e-mailadres gestuurd.`;
      case 'alreadysubscribed':
        return 'Er bestaat al een aanmelding met dit e-mailadres.';
      case 'success':
        return `Bedankt voor je aanmelding!<br>
                &nbsp;<br>
                Om ongewenste e-mail te voorkomen hebben we zojuist een bevestigingsmail naar het door jou opgegeven 
                e-mailadres gestuurd.<br>
                Je zult de nieuwsbrief pas ontvangen nadat je op de activatielink in de bevestigingsmail hebt geklikt.`;
      case 'unknown':
        return `Er is helaas iets misgegaan tijdens het aanmelden voor de nieuwsbrief.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.<br>
                &nbsp;<br>
                Foutmelding: Onbekend commando.<br>
                url: https://www.mailmens.nl/manager/json/subscribe/${newsletterID}<br>
                email: ${email}`;
      case 'notfound':
        return `Er is helaas iets misgegaan tijdens het aanmelden voor de nieuwsbrief.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.<br>
                &nbsp;<br>
                Foutmelding: Geen nieuwsbrief gevonden met het opgegeven ID.<br>
                url: https://www.mailmens.nl/manager/json/subscribe/${newsletterID}<br>
                email: ${email}`;
      case 'error':
        return `Er is helaas iets misgegaan tijdens het aanmelden voor de nieuwsbrief.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.<br>
                &nbsp;<br>
                url: https://www.mailmens.nl/manager/json/subscribe/${newsletterID}<br>
                email: ${email}`;
      case 'ajaxerror':
        return `Er is helaas iets misgegaan tijdens het aanmelden voor de nieuwsbrief.<br>
                Mocht het probleem zich blijven voordoen neem dan contact op met een beheerder.<br>
                &nbsp;<br>
                Foutmelding: AJAX request mislukt.<br>
                url: https://www.mailmens.nl/manager/json/subscribe/${newsletterID}<br>
                email: ${email}`;
      default:
        return `Onbekende fout (${status}).`;
    }
  }

  #validate() {
    if (this.#debug) console.log('validating...');

    if (this.#isSpam()) {
      if (this.#debug) console.log('>>> spam check FAILED! ending');
      Swal.fire({
        icon: 'question',
        title: 'Spam?',
        html: `Het formulier werd veel sneller opgeslagen dan mogelijk zou moeten zijn.<br>
               Invoer wordt genegeerd op verdenking van spam.`
      });
      return;
    }
    if (this.#debug) console.log('spam check PASSED! continue...');

    const newsletterID = this.#form.querySelector('[name="newsletter-id"]')?.value?.trim();
    if (!newsletterID) {
      if (this.#debug) console.log('>>> ID check FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Geen geldig nieuwsbrief ID gevonden.' });
      return;
    }

    const email = this.#form.querySelector('[name="email"]')?.value?.trim();
    if (!email) {
      if (this.#debug) console.log('>>> email check 1 FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een e-mailadres in.' });
      return;
    }
    else if (!this.#isEmail(email)) {
      if (this.#debug) console.log('>>> email check 2 FAILED! ending');
      Swal.fire({ icon: 'error', title: '', html: 'Vul een geldig e-mailadres in.' });
      return;
    }
    if (this.#debug) console.log('email check PASSED! continue...');

    if (this.#debug) console.log('all required fields are valid, submitting form...');

    const url = `https://www.mailmens.nl/manager/json/subscribe/${newsletterID}`;
    const params = new URLSearchParams({ email });

    if (this.#debug) console.log(params.toString());

    fetch(`${url}?${params.toString()}`, { method: 'GET', mode: 'cors' })
      .then(response => response.text())
      .then(response => {
        response = JSON.parse(response);
        if (this.#debug) console.log(response);
        const message = this.#getCallbackMessage(response.status, newsletterID, email);
        let icon = 'error';
        if (response.status === 'success') icon = 'success';
        else if (['inactive', 'unconfirmed', 'alreadysubscribed'].includes(response.status)) icon = 'success';
        Swal.fire({ icon, title: '', html: message });
      })
      .catch(error => {
        if (this.#debug) console.log(`error: ${error}`);
        const message = this.#getCallbackMessage('ajaxerror', newsletterID, email);
        Swal.fire({
          icon: 'error',
          title: 'Fout: formulier niet verzonden',
          html: message
        });
      });
  }
}
